import {
  TicketTemplateBlock,
  TicketTemplateLayout,
} from './logiciel-caisse-ticket-template.service';

export interface TicketTestPrintContext {
  typeCode: string;
  typeLabel: string;
  layout: TicketTemplateLayout;
  /** En-tête établissement (optionnel, sinon données démo). */
  enteteLines?: string[];
}

interface SampleTicketData {
  entete_etablissement: string[];
  code_document: string;
  date_heure: string;
  numero_caisse: string;
  vendeur: string;
  client: string;
  lignes_table: { designation: string; ref: string; qty: number; prix: string }[];
  sous_total: string;
  remise: string;
  tva: string;
  total: string;
  modes_paiement: string;
  montant_financier: string;
  motif_financier: string;
  code_barre_doc: string;
}

function sampleDataForType(typeCode: string, enteteLines?: string[]): SampleTicketData {
  const now = new Date();
  const dateHeure = now.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  const entete =
    enteteLines?.length
      ? enteteLines
      : ['BOUTIQUE DÉMO', '12 Avenue Habib Bourguiba', '1000 Tunis', 'MF: 1234567/A/M/000'];

  const base: SampleTicketData = {
    entete_etablissement: entete,
    code_document:
      typeCode === 'ticket_en_traitement'
        ? 'RES-000042'
        : typeCode === 'ticket_financier'
          ? 'FIN-000007'
          : 'TKT-000123',
    date_heure: dateHeure,
    numero_caisse: 'Caisse 01',
    vendeur: 'Vendeur Démo',
    client: 'Client passage',
    lignes_table: [
      { designation: 'T-shirt coton', ref: 'TS-001', qty: 2, prix: '45,000' },
      { designation: 'Jean slim', ref: 'JN-204', qty: 1, prix: '89,900' },
    ],
    sous_total: '179,900 TND',
    remise: '9,000 TND',
    tva: '32,382 TND',
    total: '170,900 TND',
    modes_paiement: 'Espèces: 100,000 TND\nCarte: 70,900 TND',
    montant_financier: '250,000 TND',
    motif_financier: 'Dépôt espèces — test',
    code_barre_doc: '*TKT000123*',
  };

  if (typeCode === 'ticket_en_traitement') {
    return { ...base, modes_paiement: '—', tva: '—' };
  }
  if (typeCode === 'ticket_financier') {
    return {
      ...base,
      lignes_table: [],
      sous_total: '—',
      remise: '—',
      tva: '—',
      total: base.montant_financier,
      modes_paiement: 'Espèces',
    };
  }
  return base;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function ticketStyles(widthMm: number, forPrint: boolean): string {
  const screenExtra = forPrint
    ? ''
    : `
    .ticket-root {
      margin: 0 auto;
      box-shadow: 0 2px 12px rgba(0,0,0,.12);
      background: #fff;
    }`;
  return `
    ${forPrint ? `@page { margin: 4mm; size: ${widthMm}mm auto; }` : ''}
    * { box-sizing: border-box; }
    .ticket-root {
      padding: 8px 6px;
      width: ${widthMm}mm;
      max-width: 100%;
      font-family: 'Courier New', Courier, monospace;
      font-size: 11px;
      line-height: 1.35;
      color: #111;
    }
    ${screenExtra}
    .badge-test {
      text-align: center;
      font-size: 9px;
      color: #7367f0;
      border: 1px dashed #7367f0;
      padding: 4px;
      margin-bottom: 8px;
    }
    .line { margin: 2px 0; white-space: pre-wrap; word-break: break-word; }
    .line.sub { font-size: 10px; color: #555; padding-left: 4px; }
    .line.center { text-align: center; }
    .line.bold { font-weight: 700; }
    .line.total { font-size: 12px; margin-top: 4px; }
    .lbl { color: #444; }
    .sep { text-align: center; color: #888; margin: 4px 0; }
    .muted { color: #888; font-style: italic; }
    .barcode { letter-spacing: 2px; font-size: 13px; margin: 6px 0; }
    table.lines { width: 100%; border-collapse: collapse; margin: 4px 0; font-size: 10px; }
    table.lines th, table.lines td { padding: 2px 1px; vertical-align: top; }
    table.lines th { border-bottom: 1px solid #ccc; text-align: left; }
    table.lines .c { text-align: center; width: 28px; }
    table.lines .r { text-align: right; white-space: nowrap; }
  `;
}

function renderLinesTable(
  lines: SampleTicketData['lignes_table'],
  widthMm: number
): string {
  if (!lines.length) {
    return '<div class="line muted">(aucune ligne)</div>';
  }
  const compact = widthMm <= 58;
  if (compact) {
    return lines
      .map(
        (l) =>
          `<div class="line">${escapeHtml(l.designation)}</div>` +
          `<div class="line sub">${escapeHtml(l.ref)} × ${l.qty} — ${escapeHtml(l.prix)} TND</div>`
      )
      .join('');
  }
  const rows = lines
    .map(
      (l) =>
        `<tr><td>${escapeHtml(l.designation)}</td><td class="c">${l.qty}</td><td class="r">${escapeHtml(l.prix)}</td></tr>`
    )
    .join('');
  return `<table class="lines"><thead><tr><th>Désignation</th><th class="c">Qté</th><th class="r">Prix</th></tr></thead><tbody>${rows}</tbody></table>`;
}

function renderBlock(block: TicketTemplateBlock, data: SampleTicketData, widthMm: number): string {
  if (!block.visible) {
    return '';
  }
  if (block.kind === 'separator') {
    return '<div class="sep">────────────────────────</div>';
  }
  if (block.kind === 'custom') {
    const text = block.value?.trim() || block.label;
    return `<div class="line center">${escapeHtml(text)}</div>`;
  }

  switch (block.fieldKey) {
    case 'entete_etablissement':
      return data.entete_etablissement
        .map((l) => `<div class="line center bold">${escapeHtml(l)}</div>`)
        .join('');
    case 'lignes_table':
      return renderLinesTable(data.lignes_table, widthMm);
    case 'code_barre_doc':
      return `<div class="line center barcode">${escapeHtml(data.code_barre_doc)}</div>`;
    default: {
      const key = block.fieldKey as keyof SampleTicketData;
      const raw = data[key];
      const value = Array.isArray(raw) ? raw.join('\n') : String(raw ?? '');
      const cls =
        block.fieldKey === 'total' || block.fieldKey === 'montant_financier' ? 'line bold total' : 'line';
      return `<div class="${cls}"><span class="lbl">${escapeHtml(block.label)}</span> ${escapeHtml(value)}</div>`;
    }
  }
}

function renderTicketBody(ctx: TicketTestPrintContext): { widthMm: number; body: string } {
  const layout = ctx.layout;
  const widthMm = layout.largeur_mm ?? 80;
  const data = sampleDataForType(ctx.typeCode, ctx.enteteLines);
  const blocks = [...(layout.blocks ?? [])].sort((a, b) => a.order - b.order);
  const body =
    `<div class="badge-test">APERÇU TEST — ${escapeHtml(ctx.typeLabel)}</div>` +
    blocks.map((b) => renderBlock(b, data, widthMm)).join('');
  return { widthMm, body };
}

/** HTML pour l’aperçu dans la page (modal), sans popup. */
export function buildTicketTestPreviewHtml(ctx: TicketTestPrintContext): string {
  const { widthMm, body } = renderTicketBody(ctx);
  return `<style>${ticketStyles(widthMm, false)}</style><div class="ticket-root">${body}</div>`;
}

function buildTicketTestPrintDocument(ctx: TicketTestPrintContext): string {
  const { widthMm, body } = renderTicketBody(ctx);
  return `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="utf-8" />
  <title>Test — ${escapeHtml(ctx.typeLabel)}</title>
  <style>${ticketStyles(widthMm, true)}</style>
</head>
<body>
  <div class="ticket-root">${body}</div>
</body>
</html>`;
}

/** Impression via iframe cachée — pas de fenêtre popup. */
export function printTicketTest(ctx: TicketTestPrintContext): void {
  const html = buildTicketTestPrintDocument(ctx);
  const iframe = document.createElement('iframe');
  iframe.setAttribute('title', 'Impression test ticket');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = 'none';
  iframe.style.visibility = 'hidden';
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument ?? iframe.contentWindow?.document;
  if (!doc) {
    document.body.removeChild(iframe);
    return;
  }

  doc.open();
  doc.write(html);
  doc.close();

  const win = iframe.contentWindow;
  if (!win) {
    document.body.removeChild(iframe);
    return;
  }

  const cleanup = (): void => {
    if (iframe.parentNode) {
      document.body.removeChild(iframe);
    }
  };

  win.onafterprint = cleanup;
  win.focus();
  win.print();
  window.setTimeout(cleanup, 3000);
}
